import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

const EMPTY = {
  id: "", title_en: "", title_ar: "", subtitle_en: "", subtitle_ar: "",
  image: "", body_en: "", body_ar: "",
};

export default function AdminAcademyTab() {
  const { isRTL } = useI18n();
  const qc = useQueryClient();
  const { data: tracks = [], isLoading } = useQuery({ queryKey: ["admin", "academy"], queryFn: endpoints.adminAcademy });
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState(null); // { trackId, chapter | "new" }

  const onSave = async (form) => {
    if (!editing) return;
    const { trackId, chapter } = editing;
    try {
      if (chapter === "new") {
        await endpoints.adminAddChapter(trackId, form);
        toast.success(isRTL ? "تمت الإضافة" : "Added");
      } else {
        await endpoints.adminUpdateChapter(trackId, chapter.id, form);
        toast.success(isRTL ? "تم الحفظ" : "Saved");
      }
      qc.invalidateQueries({ queryKey: ["admin", "academy"] });
      qc.invalidateQueries({ queryKey: ["academy"] });
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر الحفظ" : "Save failed"));
    }
  };

  const onDelete = async (trackId, chId) => {
    if (!window.confirm(isRTL ? "حذف هذا الدرس؟" : "Delete this lesson?")) return;
    try {
      await endpoints.adminDeleteChapter(trackId, chId);
      toast.success(isRTL ? "تم الحذف" : "Deleted");
      qc.invalidateQueries({ queryKey: ["admin", "academy"] });
      qc.invalidateQueries({ queryKey: ["academy"] });
    } catch {
      toast.error(isRTL ? "تعذّر الحذف" : "Delete failed");
    }
  };

  const moveTrack = async (trackId, dir) => {
    const ids = tracks.map((t) => t.id);
    const idx = ids.indexOf(trackId);
    if (idx < 0) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= ids.length) return;
    [ids[idx], ids[newIdx]] = [ids[newIdx], ids[idx]];
    try {
      await endpoints.adminReorderTracks(ids);
      qc.invalidateQueries({ queryKey: ["admin", "academy"] });
      qc.invalidateQueries({ queryKey: ["academy"] });
    } catch {
      toast.error(isRTL ? "تعذّر الترتيب" : "Reorder failed");
    }
  };

  const moveChapter = async (track, chId, dir) => {
    const ids = (track.chapters || []).map((c) => c.id);
    const idx = ids.indexOf(chId);
    if (idx < 0) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= ids.length) return;
    [ids[idx], ids[newIdx]] = [ids[newIdx], ids[idx]];
    try {
      await endpoints.adminReorderChapters(track.id, ids);
      qc.invalidateQueries({ queryKey: ["admin", "academy"] });
      qc.invalidateQueries({ queryKey: ["academy"] });
    } catch {
      toast.error(isRTL ? "تعذّر الترتيب" : "Reorder failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-academy-tab">
      {isLoading && <div className="text-[#5C544F]">…</div>}
      {tracks.map((track, ti) => (
        <div key={track.id} className="rounded-2xl border border-[#2A2421]/10 bg-white overflow-hidden">
          <div className="flex items-center p-4 hover:bg-[#F9F6F0]/60">
            <div className="flex flex-col gap-0.5 me-2">
              <button
                disabled={ti === 0}
                onClick={() => moveTrack(track.id, -1)}
                className="text-[#5C544F] hover:text-[#C86240] disabled:opacity-30"
                data-testid={`admin-track-up-${track.id}`}
                aria-label="Move up"
              >
                <ArrowUp className="w-4 h-4" />
              </button>
              <button
                disabled={ti === tracks.length - 1}
                onClick={() => moveTrack(track.id, 1)}
                className="text-[#5C544F] hover:text-[#C86240] disabled:opacity-30"
                data-testid={`admin-track-down-${track.id}`}
                aria-label="Move down"
              >
                <ArrowDown className="w-4 h-4" />
              </button>
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={() => setExpanded((s) => ({ ...s, [track.id]: !s[track.id] }))}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setExpanded((s) => ({ ...s, [track.id]: !s[track.id] }));
                }
              }}
              className="flex-1 flex items-center justify-between cursor-pointer"
              data-testid={`admin-track-toggle-${track.id}`}
            >
              <div className="flex items-center gap-2 text-start">
                {expanded[track.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <span className="font-semibold text-[#2A2421]">
                  {track.instructor?.title_en || track.id}
                </span>
                <span className="text-xs text-[#5C544F]">
                  · {(track.chapters || []).length} {isRTL ? "درس" : "lessons"}
                </span>
              </div>
              <Button
                size="sm"
                className="bg-[#C86240] hover:bg-[#b15637] text-white rounded-full"
                onClick={(e) => { e.stopPropagation(); setEditing({ trackId: track.id, chapter: "new" }); }}
                data-testid={`admin-add-chapter-${track.id}`}
              >
                <Plus className="w-3.5 h-3.5 me-1" /> {isRTL ? "درس جديد" : "Add lesson"}
              </Button>
            </div>
          </div>
          {expanded[track.id] && (
            <div className="border-t border-[#2A2421]/8 p-3 space-y-2">
              {(track.chapters || []).map((ch, ci) => (
                <div key={ch.id} className="flex items-start gap-3 rounded-xl bg-[#F9F6F0] p-3" data-testid={`admin-chapter-${ch.id}`}>
                  <div className="flex flex-col gap-0.5">
                    <button
                      disabled={ci === 0}
                      onClick={() => moveChapter(track, ch.id, -1)}
                      className="text-[#5C544F] hover:text-[#C86240] disabled:opacity-30"
                      data-testid={`admin-chapter-up-${ch.id}`}
                      aria-label="Move up"
                    >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      disabled={ci === (track.chapters || []).length - 1}
                      onClick={() => moveChapter(track, ch.id, 1)}
                      className="text-[#5C544F] hover:text-[#C86240] disabled:opacity-30"
                      data-testid={`admin-chapter-down-${ch.id}`}
                      aria-label="Move down"
                    >
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {ch.image && <img src={ch.image} alt="" className="w-20 h-16 rounded-lg object-cover flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#2A2421] text-sm">{ch.title_en}</div>
                    <div className="text-xs text-[#5C544F]" dir="rtl">{ch.title_ar}</div>
                    <div className="text-xs text-[#5C544F] mt-1 line-clamp-1">{ch.subtitle_en}</div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEditing({ trackId: track.id, chapter: ch })} data-testid={`admin-edit-chapter-${ch.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => onDelete(track.id, ch.id)} data-testid={`admin-delete-chapter-${ch.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      <ChapterFormDialog
        key={editing ? `${editing.trackId}-${editing.chapter === "new" ? "new" : editing.chapter?.id}` : "closed"}
        open={!!editing}
        initial={editing?.chapter === "new" ? EMPTY : editing?.chapter}
        onClose={() => setEditing(null)}
        onSave={onSave}
        isNew={editing?.chapter === "new"}
      />
    </div>
  );
}

function ChapterFormDialog({ open, initial, onClose, onSave, isNew }) {
  const { isRTL } = useI18n();
  const [form, setForm] = useState(initial || EMPTY);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-[#F9F6F0]" data-testid="admin-chapter-form">
        <DialogHeader>
          <DialogTitle className="text-start">
            {isNew ? (isRTL ? "درس جديد" : "New lesson") : (isRTL ? "تعديل الدرس" : "Edit lesson")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pe-2">
          <Field label={isRTL ? "العنوان (إنجليزي)" : "Title (EN)"}>
            <Input value={form.title_en} onChange={(e) => set("title_en", e.target.value)} data-testid="chapter-title-en-input" />
          </Field>
          <Field label={isRTL ? "العنوان (عربي)" : "Title (AR)"}>
            <Input value={form.title_ar} onChange={(e) => set("title_ar", e.target.value)} dir="rtl" data-testid="chapter-title-ar-input" />
          </Field>
          <Field label={isRTL ? "العنوان الفرعي (EN)" : "Subtitle (EN)"}>
            <Input value={form.subtitle_en || ""} onChange={(e) => set("subtitle_en", e.target.value)} />
          </Field>
          <Field label={isRTL ? "العنوان الفرعي (AR)" : "Subtitle (AR)"}>
            <Input value={form.subtitle_ar || ""} onChange={(e) => set("subtitle_ar", e.target.value)} dir="rtl" />
          </Field>
          <Field label={isRTL ? "رابط الصورة" : "Image URL"}>
            <Input value={form.image || ""} onChange={(e) => set("image", e.target.value)} placeholder="https://images.unsplash.com/..." />
          </Field>
          {form.image && <img src={form.image} alt="" className="max-h-40 rounded-lg" />}
          <Field label={isRTL ? "المحتوى (EN)" : "Body (EN)"}>
            <Textarea rows={5} value={form.body_en || ""} onChange={(e) => set("body_en", e.target.value)} />
          </Field>
          <Field label={isRTL ? "المحتوى (AR)" : "Body (AR)"}>
            <Textarea rows={5} value={form.body_ar || ""} onChange={(e) => set("body_ar", e.target.value)} dir="rtl" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-[#2A2421]/10">
          <Button variant="ghost" onClick={onClose}>{isRTL ? "إلغاء" : "Cancel"}</Button>
          <Button onClick={() => onSave(form)} className="bg-[#2A2421] text-white" data-testid="admin-save-chapter-btn">
            {isRTL ? "حفظ" : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs text-[#5C544F] mb-1">{label}</span>
    {children}
  </label>
);
